import React, { useMemo, useState } from "react";
import {
  Box, Typography, Button, TextField, InputAdornment, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Autocomplete, Stack,
  Menu, ListItemIcon, ListItemText, Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import GridViewIcon from "@mui/icons-material/GridView";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import AiImageDialog from "../../../Components/Ai/AiImageDialog";
import {
  NAVY, GOLD, PAGE_BG, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import {
  useGetTemplatesQuery,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin, selectCurrentUser } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import { extractBackendError } from "../../../Core/extractBackendError";
import ActionButtons from "../../../Components/ui/ActionButtons";
import TemplatePreviewDialog from "../TemplatePreviewDialog";

interface ICompanyOption {
  id: number;
  companyName: string;
}

interface ITemplate {
  id: number;
  name: string;
  orientation: "landscape" | "portrait";
  canvas_width: number;
  canvas_height: number;
  background: string;
  version: number;
  is_active: boolean;
  zones_count?: number;
  updated_at?: string | null;
}

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);

  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("landscape");
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  // Validation is deferred: errors only surface after a field is touched
  // (blurred while empty) or the user attempts to submit — never on open.
  const [nameTouched, setNameTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Real company DB id the template will be created for. "" = not yet chosen.
  const [companyId, setCompanyId] = useState<number | "">("");
  const [deleteTarget, setDeleteTarget] = useState<ITemplate | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  // "Create Template ▼" split-button menu + the AI image generator dialog.
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [aiImageOpen, setAiImageOpen] = useState(false);

  const { data, isLoading, isFetching } = useGetTemplatesQuery({ searchText: debounced || undefined });
  const [createTemplate, { isLoading: creating }] = useCreateTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();

  // Company options — only super admins pick a company; tenant users are
  // locked to their own, so we don't even fetch the list for them.
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin },
  );
  const companies: ICompanyOption[] = Array.isArray((companiesData as any)?.data)
    ? ((companiesData as any).data as ICompanyOption[])
    : [];
  const selectedCompany: ICompanyOption | null =
    companyId === "" ? null : (companies.find((c) => c.id === companyId) ?? null);

  const templates: ITemplate[] = useMemo(
    () => ((data as any)?.data ?? []) as ITemplate[],
    [data],
  );

  // Open the modal with a clean, non-auto-selected company for super admins;
  // tenant users are pre-locked to their own company_id.
  const openCreate = () => {
    setName("");
    setOrientation("landscape");
    setCompanyId(isSuperAdmin ? "" : (currentUser?.company_id ?? ""));
    setNameTouched(false);
    setSubmitAttempted(false);
    setCreateOpen(true);
  };

  // Every required field must be present before the primary action enables.
  const canCreate = companyId !== "" && !!name.trim() && !!orientation;

  // Whether to render each field's required-error. Deferred until interaction.
  const showNameError = (nameTouched || submitAttempted) && !name.trim();
  const showCompanyError = submitAttempted && companyId === "";

  const handleCreate = async () => {
    setSubmitAttempted(true);
    if (companyId === "") {
      showErrorMessage("Company is required.");
      return;
    }
    if (!name.trim()) return;
    const isLandscape = orientation === "landscape";
    try {
      const res: any = await createTemplate({
        company_id: Number(companyId),
        name: name.trim(),
        orientation,
        width: isLandscape ? 1920 : 1080,
        height: isLandscape ? 1080 : 1920,
        background_color: "#000000",
      }).unwrap();
      const id = res?.data?.id;
      showMessage("Template created");
      setCreateOpen(false);
      setName("");
      setCompanyId("");
      if (id) navigate(`/templates/edit/${id}`);
    } catch (e: any) {
      showErrorMessage(extractBackendError(e, "Could not create template"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.id).unwrap();
      showMessage("Template deleted");
    } catch (e: any) {
      showErrorMessage(extractBackendError(e, "Could not delete template"));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: PAGE_BG, minHeight: "calc(100vh - 64px)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <GridViewIcon sx={{ color: GOLD, fontSize: 28 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY }}>
            Templates
          </Typography>
        </Box>
        <Button
          variant="contained" color="secondary"
          startIcon={<AddIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
        >
          Create Template
        </Button>
        <Menu
          anchorEl={createMenuAnchor}
          open={Boolean(createMenuAnchor)}
          onClose={() => setCreateMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => { setCreateMenuAnchor(null); setAiImageOpen(true); }}
          >
            <ListItemIcon><AutoAwesomeIcon fontSize="small" sx={{ color: GOLD }} /></ListItemIcon>
            <ListItemText primary="Generate Image with AI" secondary="Create a background from a prompt" />
          </MenuItem>
          <MenuItem onClick={() => { setCreateMenuAnchor(null); openCreate(); }}>
            <ListItemIcon><CropOriginalIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Start from Blank Canvas" />
          </MenuItem>
          <Divider />
          <MenuItem disabled>
            <ListItemIcon><DashboardCustomizeIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Start with AI" secondary="Campaign wizard — coming soon" />
          </MenuItem>
        </Menu>
      </Box>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}` }}>
        <TextField
          size="small" fullWidth placeholder="Search templates…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: TEXT_SECONDARY, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${BORDER}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Orientation</TableCell>
              <TableCell>Canvas</TableCell>
              <TableCell>Zones</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(isLoading || isFetching) && (
              <TableRow><TableCell colSpan={6}><PageLoader /></TableCell></TableRow>
            )}
            {!isLoading && templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: TEXT_SECONDARY, textAlign: "center", py: 6 }}>
                  No templates yet — click <strong>New Template</strong> to start.
                </TableCell>
              </TableRow>
            )}
            {templates.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>{t.name}</TableCell>
                <TableCell>
                  <Chip size="small" label={t.orientation} sx={{ textTransform: "capitalize" }} />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", color: TEXT_SECONDARY }}>
                  {t.canvas_width}×{t.canvas_height}
                </TableCell>
                <TableCell>{t.zones_count ?? 0}</TableCell>
                <TableCell>
                  <Chip size="small"
                    label={t.is_active ? "active" : "inactive"}
                    color={t.is_active ? "success" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <ActionButtons
                    onView={() => setPreviewId(t.id)}
                    onEdit={() => navigate(`/templates/edit/${t.id}`)}
                    onDelete={() => setDeleteTarget(t)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Template</DialogTitle>
        {/* One consistent vertical layout (Stack) for all three fields.
            `mt` on the Stack gives the first field's floating outlined label
            clearance from the title so it never overlaps. Order: Company,
            Name, Orientation. */}
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            {/* 1 — Company (required) */}
            {isSuperAdmin ? (
              <Autocomplete
                fullWidth
                options={companies}
                value={selectedCompany}
                onChange={(_, v) => setCompanyId(v ? v.id : "")}
                getOptionLabel={(o) => o.companyName}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company *"
                    placeholder="— Select a company —"
                    error={showCompanyError}
                    helperText={showCompanyError ? "Company is required." : undefined}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                label="Company *"
                value={currentUser?.company?.name ?? "—"}
                InputProps={{ readOnly: true }}
                disabled
                helperText="Templates are created for your company."
              />
            )}

            {/* 2 — Name (required) */}
            <TextField
              autoFocus fullWidth label="Name *" value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={showNameError}
              helperText={showNameError ? "Name is required." : undefined}
            />

            {/* 3 — Orientation (required) */}
            <FormControl fullWidth>
              <InputLabel>Orientation *</InputLabel>
              <Select
                label="Orientation *" value={orientation}
                onChange={(e) => setOrientation(e.target.value as "landscape" | "portrait")}
              >
                <MenuItem value="landscape">Landscape (1920×1080)</MenuItem>
                <MenuItem value="portrait">Portrait (1080×1920)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" color="secondary"
            onClick={handleCreate} disabled={creating || !canCreate}>
            Create &amp; Open Designer
          </Button>
        </DialogActions>
      </Dialog>

      <DeletePopup
        open={!!deleteTarget}
        title="Delete template?"
        content={`This will permanently delete "${deleteTarget?.name ?? ""}".`}
        handleCloseDelete={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <TemplatePreviewDialog
        templateId={previewId}
        onClose={() => setPreviewId(null)}
      />

      <AiImageDialog
        open={aiImageOpen}
        onClose={() => setAiImageOpen(false)}
        isSuperAdmin={isSuperAdmin}
        companies={companies}
        defaultCompanyId={isSuperAdmin ? "" : (currentUser?.company_id ?? "")}
      />
    </Box>
  );
};

export default TemplateList;
